// Global auth interceptors — attach the bearer token to EVERY request that goes
// to our API, no matter whether the call site uses axios or raw fetch.
//
// Call sites are spread across ~25 screens and each one used to build its own
// headers; several of them (e.g. /booking/plans, /booking/get-invoices) sent no
// Authorization header at all and the API answers `"you are not authorise"`.
// Installing this once at app start-up means a call site can never forget again.
//
// Rules:
//  - only requests to API_PATHS.BASE_URL are touched (Google Maps / proxy calls
//    keep working untouched)
//  - an Authorization header already set by the call site always wins
//  - `clientToken` (admin acting as a parent) takes priority over `token`
//  - when there is no token at all, nothing is added
/* global globalThis */
import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import API_PATHS from './ApiPath';

// Endpoints that must stay unauthenticated (they issue the tokens).
const PUBLIC_PATHS = [API_PATHS.LOGIN_API, API_PATHS.CREATE_PARENT];

const isApiUrl = url =>
  typeof url === 'string' && url.startsWith(API_PATHS.BASE_URL);

const isPublicUrl = url => PUBLIC_PATHS.some(path => url.includes(path));

export const getAuthToken = async () => {
  try {
    const clientToken = await AsyncStorage.getItem('clientToken');
    const token = await AsyncStorage.getItem('token');
    return clientToken || token || null;
  } catch (error) {
    console.error('Error retrieving token:', error);
    return null;
  }
};

const headersHaveAuth = headers => {
  if (!headers) {
    return false;
  }
  if (typeof headers.get === 'function') {
    return !!headers.get('Authorization');
  }
  if (Array.isArray(headers)) {
    return headers.some(([key]) => String(key).toLowerCase() === 'authorization');
  }
  return Object.keys(headers).some(
    key => key.toLowerCase() === 'authorization' && headers[key],
  );
};

// ---------------------------------------------------------------- axios ----

const installAxiosInterceptor = () => {
  axios.interceptors.request.use(async config => {
    const url = `${config.baseURL || ''}${config.url || ''}`;
    if (!isApiUrl(url) || isPublicUrl(url) || headersHaveAuth(config.headers)) {
      return config;
    }

    const token = await getAuthToken();
    if (!token) {
      return config;
    }

    if (config.headers && typeof config.headers.set === 'function') {
      config.headers.set('Authorization', `Bearer ${token}`);
    } else {
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
    }
    return config;
  });
};

// ---------------------------------------------------------------- fetch ----

// Normalises Headers / array / plain-object into a plain object we can extend.
const toPlainHeaders = headers => {
  if (!headers) {
    return {};
  }
  if (typeof headers.forEach === 'function' && typeof headers.get === 'function') {
    const plain = {};
    headers.forEach((value, key) => {
      plain[key] = value;
    });
    return plain;
  }
  if (Array.isArray(headers)) {
    return headers.reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  }
  return { ...headers };
};

const installFetchInterceptor = () => {
  const globalScope = globalThis;
  if (typeof globalScope.fetch !== 'function' || globalScope.fetch.__authPatched) {
    return;
  }

  const originalFetch = globalScope.fetch.bind(globalScope);

  const patchedFetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input && input.url;

    if (!isApiUrl(url) || isPublicUrl(url)) {
      return originalFetch(input, init);
    }

    const initHeaders = init && init.headers;
    const requestHeaders = typeof input !== 'string' ? input && input.headers : null;
    if (headersHaveAuth(initHeaders) || headersHaveAuth(requestHeaders)) {
      return originalFetch(input, init);
    }

    const token = await getAuthToken();
    if (!token) {
      return originalFetch(input, init);
    }

    const headers = {
      ...toPlainHeaders(requestHeaders),
      ...toPlainHeaders(initHeaders),
      Authorization: `Bearer ${token}`,
    };

    return originalFetch(input, { ...(init || {}), headers });
  };

  patchedFetch.__authPatched = true;
  globalScope.fetch = patchedFetch;
};

let installed = false;

export const installAuthInterceptors = () => {
  if (installed) {
    return;
  }
  installed = true;
  installAxiosInterceptor();
  installFetchInterceptor();
};

export default installAuthInterceptors;
